import {Sell, SellPaymentMethod} from "../sell.entity";

import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  OutlinedInput,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {grey} from "@mui/material/colors";
import {sellRepository} from "../sell.repository";
import moment from "moment";
import application from "../../core/application";
import {productRepository} from "../../product/product.repository";
import {SellUnregisterProduct} from "../sell-unregister-product";
import AttachMoney from "@mui/icons-material/AttachMoney";
import CreditCard from "@mui/icons-material/CreditCard";
import QrCode2 from "@mui/icons-material/QrCode2";
import RequestQuote from "@mui/icons-material/RequestQuote";
import ReceiptLong from "@mui/icons-material/ReceiptLong";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ArrowBack from "@mui/icons-material/ArrowBack";
import type {SvgIconComponent} from "@mui/icons-material";
import { customerCreditService } from "../../customer/customer-credit.service";
import Swal from "sweetalert2";
import { Permission } from "../../users/user.entity";
import { useModuleAccess } from "../../settings/module-access";
import { analyticsService } from "../../core/analytics";

type FinishSellComponentProps = {
  sell: Sell;
  /** Vincula a venda à sessão de caixa ao concluir */
  cashSessionId?: string | null;
  onCompleteSell: () => void;
  onClose: () => void;
  closeSell: () => void;
};

type PaymentOption = {
  title: string;
  subtitle: string;
  method: SellPaymentMethod;
  Icon: SvgIconComponent;
  color: string;
  emoji: string;
};

const paymentOptions: PaymentOption[] = [
  {
    title: "Dinheiro",
    subtitle: "Informe o valor recebido para calcular o troco",
    method: SellPaymentMethod.MONEY,
    Icon: AttachMoney,
    color: "#059669",
    emoji: "💵",
  },
  {
    title: "Cartão",
    subtitle: "Crédito ou débito",
    method: SellPaymentMethod.CREDIT_CARD_DEBIT,
    Icon: CreditCard,
    color: "#2563eb",
    emoji: "💳",
  },
  {
    title: "PIX",
    subtitle: "Pagamento instantâneo",
    method: SellPaymentMethod.PIX,
    Icon: QrCode2,
    color: "#7c3aed",
    emoji: "⚡",
  },
  {
    title: "Fiado",
    subtitle: "Lançar dívida para o cliente selecionado",
    method: SellPaymentMethod.CREDIT,
    Icon: RequestQuote,
    color: "#b45309",
    emoji: "🧾",
  },
];

export const FinishSellComponent = ({
  sell,
  cashSessionId = null,
  onCompleteSell,
  onClose,
  closeSell,
}: FinishSellComponentProps) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(0);
  const [moneyInput, setMoneyInput] = useState(false);
  const [money, setMoney] = useState(0.0);
  const [moneyDisplay, setMoneyDisplay] = useState("");
  const [change, setChange] = useState<number | null>(null);
  const [completed, setCompleted] = useState(false);
  const [user] = application.user.useState();
  const moduleAccess = useModuleAccess();
  const availablePaymentOptions = useMemo(
    () =>
      paymentOptions.filter(
        (option) =>
          option.method !== SellPaymentMethod.CREDIT ||
          (user?.hasPermission(Permission.ALLOW_CREDIT_SALE) &&
            moduleAccess.activeCustomersTier !== null)
      ),
    [moduleAccess.activeCustomersTier, user]
  );

  const totalFormatted = sell.getSellTotal().toLocaleString("pt-BR", {
    currency: "BRL",
    style: "currency",
  });

  const finishSell = async () => {
    if (sell.getPaymentMethod() === SellPaymentMethod.CREDIT && !sell.getCustomerId()) {
      await Swal.fire({
        title: "Cliente obrigatório",
        text: "Selecione ou cadastre um cliente antes de vender fiado.",
        icon: "warning",
      });
      return;
    }

    sell.setCompletedAt(moment());
    if (cashSessionId) {
      sell.setCashSessionId(cashSessionId);
    }

    await Promise.all(
      sell
        .getProducts()
        .getValues()
        .filter((product) => !(product.entity instanceof SellUnregisterProduct))
        .map((product) => {
          product.entity.decrementStock(product.quantity);
          return productRepository.save(product.entity);
        })
    );

    await sellRepository.save(sell);

    if (sell.getPaymentMethod() === SellPaymentMethod.CREDIT) {
      await customerCreditService.createDebtForSale(
        sell.getCustomerId() as string,
        sell.id,
        sell.getSellTotal()
      );
    }

    analyticsService.trackEvent("sale_completed", {
      payment_method: sell.getPaymentMethod(),
      products_count: sell.getProducts().getValues().length,
      total_value: sell.getSellTotal(),
      has_customer: Boolean(sell.getCustomerId()),
      linked_to_cash_session: Boolean(cashSessionId),
    });

    setCompleted(true);
    onCompleteSell();
  };

  const confirmMethod = useCallback(
    async (index?: number) => {
      const method = availablePaymentOptions[index ?? selectedPaymentMethod].method;
      if (method === SellPaymentMethod.MONEY) {
        setMoneyInput(true);
        return;
      }

      sell.setPaymentMethod(method);
      try {
        await finishSell();
      } catch (error) {
        console.error(error);
        await Swal.fire({
          title: "Erro",
          text: error instanceof Error ? error.message : "Não foi possível finalizar a venda.",
          icon: "error",
        });
      }
    },
    [availablePaymentOptions, selectedPaymentMethod, sell]
  );

  const confirmMoney = useCallback(async () => {
    sell.setPaymentMethod(SellPaymentMethod.MONEY);

    if (money > 0) {
      setChange(money - sell.getSellTotal());
    }

    try {
      await finishSell();
    } catch (error) {
      console.error(error);
      await Swal.fire({
        title: "Erro",
        text: error instanceof Error ? error.message : "Não foi possível finalizar a venda.",
        icon: "error",
      });
    }
  }, [money, sell]);

  useEffect(() => {
    const keyEvent = (event: KeyboardEvent) => {
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (event.key === "ArrowUp") {
        setSelectedPaymentMethod((selected) => {
          if (selected === 0) {
            return availablePaymentOptions.length - 1;
          }

          return selected - 1;
        });
        return;
      }

      if (event.key === "ArrowDown") {
        setSelectedPaymentMethod((selected) => {
          if (selected === availablePaymentOptions.length - 1) {
            return 0;
          }

          return selected + 1;
        });
        return;
      }

      if (event.key === "Enter") {
        if (completed) {
          closeSell();
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (moneyInput) {
          confirmMoney();
          return;
        }

        confirmMethod();
        return;
      }

      if (event.key === "Escape") {
        if (completed) {
          closeSell();
        }
      }
    };

    document.addEventListener("keyup", keyEvent);

    return () => document.removeEventListener("keyup", keyEvent);
  }, [availablePaymentOptions.length, change, confirmMethod, confirmMoney, moneyInput, completed, closeSell]);

  if (completed) {
    return (
      <Card
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          maxWidth: 440,
          mx: "auto",
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            background: (t) =>
              `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
            color: "primary.contrastText",
            px: 3,
            py: 3,
            borderRadius: "12px 12px 0 0",
            textAlign: "center",
          }}
        >
          <CheckCircle sx={{fontSize: 56, opacity: 0.95, mb: 1}} />
          <Typography variant="h5" fontWeight={800}>
            🎉 Venda concluída!
          </Typography>
          <Typography variant="body2" sx={{opacity: 0.9, mt: 0.5}}>
            Tudo certo — venda registrada com sucesso.
          </Typography>
        </Box>
        <CardContent>
          {change !== null && money > 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                borderColor: "success.light",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Troco para o cliente
              </Typography>
              <Typography variant="h6" color="success.dark" fontWeight={800}>
                {change.toLocaleString("pt-BR", {
                  currency: "BRL",
                  style: "currency",
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pago em dinheiro: {moneyDisplay}
              </Typography>
            </Paper>
          ) : null}
          <Stack spacing={1.5}>
            <Button fullWidth variant="contained" size="large" onClick={() => closeSell()}>
              Concluir
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (moneyInput) {
    return (
      <Card
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          maxWidth: 440,
          mx: "auto",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: (t) => alpha(t.palette.success.main, 0.12),
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney color="success" sx={{fontSize: 32}} />
            <Box>
              <Typography variant="h6" fontWeight={800}>
                💵 Quanto o cliente pagou?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Digite o valor em dinheiro — calculamos o troco automaticamente.
              </Typography>
            </Box>
          </Stack>
        </Box>
        <CardContent>
          <Paper
            variant="outlined"
            sx={{p: 2, mb: 2, bgcolor: "grey.50"}}
          >
            <Typography variant="caption" color="text.secondary">
              Total da venda
            </Typography>
            <Typography variant="h5" fontWeight={800} color="primary">
              {totalFormatted}
            </Typography>
          </Paper>
          <FormControl fullWidth sx={{mb: 2}}>
            <InputLabel htmlFor="money">Valor recebido</InputLabel>
            <OutlinedInput
              id="money"
              label="Valor recebido"
              value={moneyDisplay}
              onBlur={(event) => event.target.focus()}
              autoFocus
              startAdornment={
                <InputAdornment position="start">R$</InputAdornment>
              }
              onChange={(event) => {
                let value = event.target.value.replace(/[^0-9]/g, "");
                setMoney(parseFloat(value) / 100);
                value = new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })
                  .format(Number(value) / 100)
                  .replaceAll("[^0123456789.,]", "");
                setMoneyDisplay(value);
              }}
            />
          </FormControl>
          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              color="primary"
              onClick={() => confirmMoney()}
            >
              <Chip label="Enter" size="small" sx={{bgcolor: grey[400], mr: 1}} />
              Confirmar e finalizar
            </Button>
            <Button
              fullWidth
              startIcon={<ArrowBack />}
              onClick={() => {
                setMoneyInput(false);
                setMoneyDisplay("");
                setMoney(0);
              }}
            >
              Voltar aos meios de pagamento
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        maxWidth: 480,
        mx: "auto",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: (t) =>
            `linear-gradient(120deg, ${alpha(t.palette.primary.main, 0.12)} 0%, ${alpha(t.palette.secondary.main, 0.08)} 100%)`,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ReceiptLong color="primary" sx={{fontSize: 36}} />
          <Box>
            <Typography variant="h6" fontWeight={800}>
              🧾 Como vai pagar?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toque na opção ou use ↑ ↓ e Enter
            </Typography>
          </Box>
        </Stack>
      </Box>
      <CardContent>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            background: (t) =>
              `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.15)} 0%, ${alpha(t.palette.primary.main, 0.05)} 100%)`,
            border: 1,
            borderColor: (t) => alpha(t.palette.primary.main, 0.25),
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Total a receber
          </Typography>
          <Typography variant="h4" fontWeight={800} color="primary.dark">
            {totalFormatted}
          </Typography>
        </Paper>
        {sell.getCustomerNameSnapshot() ? (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Cliente
            </Typography>
            <Typography fontWeight={700}>{sell.getCustomerNameSnapshot()}</Typography>
          </Paper>
        ) : null}

        <Stack spacing={1.25} sx={{mb: 2}}>
          {availablePaymentOptions.map((opt, index) => {
            const Icon = opt.Icon;
            const selected = selectedPaymentMethod === index;
            return (
              <Paper
                key={opt.method}
                elevation={selected ? 2 : 0}
                sx={{
                  p: 0,
                  overflow: "hidden",
                  border: 2,
                  borderColor: selected ? "primary.main" : "divider",
                  cursor: "pointer",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: 2,
                  },
                }}
                onClick={() => confirmMethod(index)}
              >
                <ListItemButton
                  selected={selected}
                  sx={{
                    py: 2,
                    alignItems: "flex-start",
                    "&.Mui-selected": {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: (t) => alpha(opt.color, 0.18),
                        color: opt.color,
                        width: 48,
                        height: 48,
                      }}
                    >
                      <Icon sx={{fontSize: 26}} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={700} component="span">
                        {opt.emoji} {opt.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {opt.subtitle}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </Paper>
            );
          })}
        </Stack>

        <Stack spacing={1.5}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => confirmMethod()}
          >
            <Chip label="Enter" size="small" sx={{bgcolor: grey[400], mr: 1}} />
            Confirmar seleção
          </Button>
          <Button
            fullWidth
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => onClose()}
          >
            Voltar à venda
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};
